import React, { useCallback, useMemo, useRef } from 'react';
import { View, Animated, ScrollView, Text, StyleSheet } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import { ItemsListWrapperProps, ItemWrapperProps } from '@v2/pages/machines/common/types/props';
import { controlAPICall, formatStartOf, isPhone, isWeb, localTime } from '@helpers';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import useStatusBar from '@components/common/useStatusBar';
import { notchTop, scale } from '@components/frame';
import FeatureFlags from '@components/featureFlags';
import Header from '@components/machines/components/header/List';
import StatusBlock from '@components/machines/components/header/components/StatusBlock';
import { SmallLoading } from '@components/loading';
import { WithColorProps } from '@styles/colors/types';
import { WebDefaultStatusBlock } from '@v2/pages/machines/common/components/WebDefaultStatusBlock/WebDefaultStatusBlock';
import { getStatusSegments } from '@v2/pages/machines/common/helpers/getStatusSegments';
import ItemWrapper from '@v2/pages/machines/common/wrappers/ItemsWrapper/ItemWrapper';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { connect } from 'react-redux';
import { useSelectMachine } from '@v2/pages/machines/hooks/useSelectMachine';
import { setSelectedGroup, setStatusesCount } from '@reducers/machinesV2';
import { getMachineStatistics, setSelectedMachine } from '@reducers/machines';
import { ALL_MACHINES_OBJECT, DATE_FORMAT, PHONE_LIST_HEADER_MARGIN_TOP } from '@v2/pages/common/constants/base.constants';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';

interface Props extends ItemsListWrapperProps, WithColorProps {}

const TokenInfoComponent = ({ machines, C }) => {
  return (
    <FeatureFlags {...{ name: 'general-showapiresponsetime', C }} key="request-time">
      <View style={{ position: 'absolute', zIndex: 1000000, top: scale(35), left: scale(20) }}>
        <Text style={{ color: C('Label/Light/Quinary'), fontSize: 12, marginBottom: 10 }}>
          Request - ({machines.requestTimeEndMS} ms) |{' '}
          {machines && machines.refreshInfo && machines.refreshInfo.start
            ? `Refresh token - ${machines.refreshInfo.start} - ${machines.refreshInfo.start}`
            : 'Token is valid'}
        </Text>
      </View>
    </FeatureFlags>
  );
};

export const TokenInfo = connect(({ machines }) => ({ machines }), {})(ColorWrapper(TokenInfoComponent));

const ListHeaderComponent = ({ selectedGroup, setSelectedGroup, C, machines: data, statusesCount, selectedItem }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const marginTop = isPhone ? scale(PHONE_LIST_HEADER_MARGIN_TOP + notchTop) : 50;
  const animationDuration = scale(100);

  const { shouldDisplayNowScreenList } = useHandleNavigationMachinesHeader();
  const { isAllSelected } = useHandleParams();

  useFocusEffect(
    React.useCallback(() => {
      if (data.length) {
        setStatusesCount(data);
      }
    }, [data])
  );

  const status = useMemo(() => getStatusSegments(C, statusesCount), [statusesCount]);
  const title = !isPhone ? i18n.t('Machines') : isAllSelected ? i18n.t('Machines') : selectedItem?.shortName;

  return (
    <Animated.View
      style={{
        transform: [
          {
            translateY: fadeAnim.interpolate({
              inputRange: [-1, 0, animationDuration, animationDuration + 1],
              outputRange: [0, 0, -animationDuration, -animationDuration],
            }),
          },
        ],
        width: '100%',
        position: 'absolute',
        backgroundColor: C('Background / Light / System'),
        zIndex: 1000,
      }}
    >
      <Header
        {...{
          text: title,
          fadeAnim,
          marginTop,
          animationDuration,
          selectedGroup,
          setSelectedGroup,
        }}
        key="header"
      />
      {shouldDisplayNowScreenList && <StatusBlock {...{ status }} />}
    </Animated.View>
  );
};

export const ListHeader = connect(
  ({ machines: { machines, selectedItem }, machinesV2: { selectedGroup, statusesCount } }) => ({
    selectedGroup,
    machines,
    statusesCount,
    selectedItem,
  }),
  { setSelectedGroup, setStatusesCount }
)(ColorWrapper(ListHeaderComponent));

const ItemsListWrapper = (props: Props) => {
  const {
    listWidth,
    data,
    additionalData = {},
    selectedItem,
    setSelectedMachine,
    timeOffset,
    C,
    machines = {},
    generateImageURL,
    initialLoading,
    machinesLoaded,
    selectedGroup,
    showLoading,
    getItemElement,
    statusesCount,
    setStatusesCount,
    selectedChartDate,
    getMachineStatistics,
  } = props;

  useSelectMachine(props.data);

  const route = useRoute();
  const shouldUseMachineImage = !route.name?.includes(MachinesScreen.LIVE);

  const abortControllerMachine = useRef();

  useFocusEffect(
    useCallback(() => {
      return () => {
        abortControllerMachine.current?.abort();
      };
    }, [])
  );

  const { isAllSelected: allSelected, timePeriod: selectedTime } = useHandleParams();

  const { shouldDisplayAllMachinesButton, shouldDisplayNowScreenList } = useHandleNavigationMachinesHeader();

  useStatusBar({
    C,
  });

  useFocusEffect(
    React.useCallback(() => {
      if (data.length) {
        setStatusesCount(data);
      }
    }, [data])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (machines.machines && props.data.length === 0) {
        const momentStart = selectedChartDate ? localTime(selectedChartDate) : localTime();

        const d = { period: selectedTime, date: momentStart.startOf(formatStartOf(selectedTime)).format(DATE_FORMAT) };
        controlAPICall(getMachineStatistics, d, abortControllerMachine);
      }
    }, [machines.machines, props.data.length, !!selectedItem, selectedChartDate])
  );

  const status = useMemo(() => getStatusSegments(C, statusesCount), [statusesCount]);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const marginTop = isPhone ? scale(PHONE_LIST_HEADER_MARGIN_TOP + notchTop) : 50;

  const displayTime = shouldDisplayNowScreenList;

  const existedMachineIds = Array.isArray(machines?.machines) ? machines.machines.map((m) => m?.machineId) : [];
  const machineIdsHash = existedMachineIds.join(',');

  const selectedGroupIDs = selectedGroup?.machines?.map((i) => Number(i?.machineId)) || [];
  
  const renderArray = useMemo(() => {
    return data
      .filter((i) => existedMachineIds.includes(i.id) && (selectedGroup ? selectedGroupIDs.includes(Number(i.id)) : true))
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [data, selectedGroup, selectedGroupIDs, machineIdsHash]);

  const onPressAllMachines = () => {
    setSelectedMachine(ALL_MACHINES_OBJECT);
  };

  const isFirstRender = useRef<boolean>(true);

  useFocusEffect(
    useCallback(() => {
      if (!showLoading && machinesLoaded) {
        isFirstRender.current = false;
      }
    }, [showLoading, machinesLoaded])
  );

  return [
    !isPhone && <TokenInfo key="token-info" />,
    <View
      style={[
        {
          width: isPhone ? '100%' : listWidth,
          backgroundColor: C('Background/Light/System'),
          borderRightColor: C('Hairline / Light / No trans'),
          borderRightWidth: isPhone ? 0 : isWeb ? 1 : StyleSheet.hairlineWidth,
        },
      ]}
      key="machines-scroll"
    >
      <ListHeader />
      <ScrollView
        scrollEventThrottle={8}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: fadeAnim } } }], {
          useNativeDriver: false,
        })}
      >
        <Header
          {...{
            text: i18n.t('Machines'),
            dummy: true,
            marginTop,
          }}
          key="header"
        />
        {shouldDisplayNowScreenList && <StatusBlock {...{ status, dummy: true }} />}
        {shouldDisplayAllMachinesButton && !isPhone && <WebDefaultStatusBlock {...{ C, onPress: onPressAllMachines, allSelected }} />}
        {isFirstRender.current && (showLoading || !machinesLoaded) ? (
          <View style={{ width: '100%', marginTop: scale(150) }}>
            <SmallLoading />
          </View>
        ) : (
          renderArray?.map((item, index) => {
            const itemData = {
              ...item,
              machineStatus: item?.machineStatus ?? {},
              ...additionalData,
            };

            const itemWrapperProps: ItemWrapperProps = {
              generateImageURL,
              item: itemData,
              setSelectedMachine,
              timeOffset,
              nextSelected: renderArray[index + 1] ? renderArray[index + 1].id === selectedItem?.id : false,
              selected: selectedItem?.id === item?.id && !allSelected,
              loading: (!machinesLoaded || initialLoading) && isFirstRender.current,
              forceRenderWhenDataUpdated: !machinesLoaded || initialLoading,
              displayTime,
              listWidth,
              C,
            };

            return (
              <View style={{ position: 'relative' }} key={`listitem-${item?.machineId}`}>
                <ItemWrapper {...itemWrapperProps} shouldUseMachineImage={shouldUseMachineImage}>
                  {getItemElement(itemWrapperProps)}
                </ItemWrapper>
              </View>
            );
          })
        )}
      </ScrollView>
    </View>,
  ];
};

export default connect(
  ({ machinesV2: { initialLoading, selectedGroup, statusesCount, selectedChartDate } }) => ({
    initialLoading,
    selectedGroup,
    statusesCount,
    selectedChartDate,
  }),
  {
    setStatusesCount,
    setSelectedMachine,
    getMachineStatistics,
  }
)(ColorWrapper(ItemsListWrapper));
